const decimal = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 });

const observation = (payload, key) => {
  const item = payload?.available && Array.isArray(payload.indicators)
    ? payload.indicators.find((candidate) => candidate?.key === key)
    : null;
  if (item?.value == null || item.value === "") return null;
  const value = Number(item?.value);
  return item && Number.isFinite(value) ? { ...item, value } : null;
};

const signedPercent = (value) => `${value > 0 ? "+" : ""}${decimal.format(value)} %`;

const sourceLabel = (item) => {
  const provider = item?.source?.provider;
  if (!provider) return null;
  return `${provider}${item.period ? ` (${item.period})` : ""}`;
};

const signal = ({ id, title, insight, priority, evolution, macro, macroLabel, extraMetrics = [], extraSources = [] }) => ({
  id,
  category: "Macro × tourisme",
  title,
  insight,
  priority,
  metrics: [
    { label: "Arrivées", value: signedPercent(evolution) },
    { label: macroLabel, value: signedPercent(macro.value) },
    ...extraMetrics,
  ],
  sources: Array.from(new Set(["Lodge Data", sourceLabel(macro), ...extraSources].filter(Boolean))),
});

/**
 * Derive at most one macro-tourism signal from published observations.
 *
 * The thresholds are deliberately conservative and the function has no fallback:
 * without an observed tourism evolution and an official macro observation it
 * returns no signal.
 */
export const buildMacroTourismSignals = ({ market, economic }) => {
  if (market?.evolution == null || market.evolution === "") return [];
  const evolution = Number(market?.evolution);
  if (!Number.isFinite(evolution)) return [];

  const growth = observation(economic, "gdp_growth");
  const inflation = observation(economic, "inflation");
  const marketName = market?.name || "Le marché sélectionné";

  if (evolution <= -5 && growth?.value > 0 && (!inflation || inflation.value < 5)) {
    return [signal({
      id: "macro-tourism-demand-divergence",
      title: "Recul touristique malgré un contexte économique favorable",
      insight: `${marketName} recule de ${decimal.format(Math.abs(evolution))} % alors que la croissance du PIB reste positive${inflation ? " et l’inflation contenue" : ""}. La faiblesse observée paraît donc plus spécifique au marché touristique qu’au cycle macroéconomique général.`,
      priority: evolution <= -10 ? "Élevé" : "Moyen",
      evolution,
      macro: growth,
      macroLabel: "Croissance PIB",
      extraMetrics: inflation ? [{ label: "Inflation", value: signedPercent(inflation.value) }] : [],
      extraSources: inflation ? [sourceLabel(inflation)] : [],
    })];
  }

  const adverseMacro = growth?.value <= 0 ? growth : (inflation?.value >= 5 ? inflation : null);
  if (evolution >= 5 && adverseMacro) {
    const macroLabel = adverseMacro.key === "inflation" ? "Inflation" : "Croissance PIB";
    return [signal({
      id: "macro-tourism-demand-resilience",
      title: "Demande touristique résiliente malgré le contexte économique",
      insight: `${marketName} progresse de ${decimal.format(evolution)} % malgré un indicateur macroéconomique défavorable. Cette résilience mérite d’être consolidée et suivie avant toute extrapolation.`,
      priority: "Moyen",
      evolution,
      macro: adverseMacro,
      macroLabel,
    })];
  }

  if (evolution <= -5 && adverseMacro) {
    const macroLabel = adverseMacro.key === "inflation" ? "Inflation" : "Croissance PIB";
    return [signal({
      id: "macro-tourism-demand-headwinds",
      title: "Recul touristique renforcé par des vents contraires macroéconomiques",
      insight: `${marketName} recule de ${decimal.format(Math.abs(evolution))} % tandis qu’un indicateur macroéconomique du marché émetteur est défavorable. Prioriser les actions mesurables et surveiller la sensibilité prix.`,
      priority: evolution <= -10 ? "Élevé" : "Moyen",
      evolution,
      macro: adverseMacro,
      macroLabel,
    })];
  }

  return [];
};
